import { useCallback, useRef, useState } from 'react';

/**
 * Sauvegarde automatique d'un écran de réglages.
 *
 * LA RÈGLE, posée le 2026-09-22 : ce qui rend public, ou ce qui écrit chez
 * quelqu'un d'autre, garde un geste explicite. Le reste se sauve tout seul.
 *
 * Un INTERRUPTEUR est déjà un geste d'engagement — sa position EST l'état.
 * Demander de le confirmer par un second bouton n'ajoute aucune protection :
 * ça crée l'écart entre ce que l'écran montre et ce que la base contient.
 * C'est exactement ce qui s'est produit avec `slot_selection_enabled`, qui
 * attendait « Enregistrer » pendant que la fenêtre des créneaux, juste à côté,
 * écrivait immédiatement.
 *
 * Un CHAMP TEXTE n'a pas de geste d'engagement : on tape, on hésite, on
 * efface. Il se sauve donc au BLUR, jamais à la frappe — et seulement s'il a
 * changé, pour ne pas écrire à chaque passage de focus.
 *
 * ⚠️ Un auto-save sans retour visible échange la friction contre l'échec
 * silencieux, ce qui est pire. D'où, toujours : l'état affiché à l'écran
 * (`AutoSaveIndicator`), et la remise en arrière quand le serveur refuse — un
 * interrupteur qui reste « allumé » après un 403 ferait croire à un réglage
 * actif qui ne l'est pas.
 */
export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface Options {
  /** Message d'erreur à afficher ; sinon l'indicateur montre son libellé générique. */
  onError?: (error: unknown) => void;
}

export interface AutoSave<P extends object> {
  status: AutoSaveStatus;
  /** Relance la dernière écriture échouée ; `null` tant qu'il n'y en a pas. */
  retry: (() => void) | null;
  /** Écrit maintenant. `revert` remet l'écran dans son état d'avant sur refus. */
  commit: (patch: P, revert?: () => void) => Promise<void>;
  /** Écrit seulement si la valeur diffère de la dernière connue du serveur. */
  commitIfChanged: <K extends keyof P>(key: K, value: P[K], revert?: () => void) => Promise<void>;
  /** Ce que le serveur porte aujourd'hui — à poser au chargement de l'écran. */
  setBaseline: (values: Partial<P>) => void;
}

export function useAutoSave<P extends object>(
  save: (patch: P) => Promise<void>,
  options?: Options,
): AutoSave<P> {
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const [retry, setRetry] = useState<(() => void) | null>(null);
  const baseline = useRef<Partial<P>>({});
  // Deux écritures peuvent se croiser (un interrupteur cliqué pendant qu'un
  // champ texte part au blur). Seule la PLUS RÉCENTE a le droit de poser
  // l'état affiché : sinon un « Enregistré » tardif effacerait une erreur.
  const sequence = useRef(0);

  const setBaseline = useCallback((values: Partial<P>) => {
    baseline.current = { ...baseline.current, ...values };
  }, []);

  const commit = useCallback(async (patch: P, revert?: () => void) => {
    const mine = ++sequence.current;
    setStatus('saving');
    setRetry(null);
    try {
      await save(patch);
      baseline.current = { ...baseline.current, ...patch };
      if (sequence.current === mine) setStatus('saved');
    } catch (error) {
      revert?.();
      if (sequence.current === mine) {
        setStatus('error');
        setRetry(() => () => { void commit(patch, revert); });
      }
      options?.onError?.(error);
    }
    // `options` volontairement hors des dépendances : les appelants passent un
    // objet littéral, le remettre ici recréerait `commit` à chaque rendu et
    // ferait repartir tous les effets qui en dépendent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [save]);

  const commitIfChanged = useCallback(
    async <K extends keyof P>(key: K, value: P[K], revert?: () => void) => {
      if (Object.is(baseline.current[key], value)) return;
      await commit({ [key]: value } as unknown as P, revert);
    },
    [commit],
  );

  return { status, retry, commit, commitIfChanged, setBaseline };
}

/**
 * Un champ date/heure à moitié tapé ne se sauve pas.
 *
 * React voit une valeur VIDE sur un `datetime-local` incomplet, mais le
 * navigateur, lui, le sait (`validity.badInput`). Sans ce garde, quitter un
 * champ où l'on a saisi l'heure sans la date écrirait `null` en base — le
 * défaut corrigé le 2026-09-09 sur les 22 champs date de l'app, qu'un blur
 * automatique ferait revenir par une autre porte.
 */
export const saisieDateIncomplete = (e: { target: unknown }): boolean => {
  const cible = e.target as HTMLInputElement | null;
  return !!cible?.validity?.badInput;
};
